import React, { useState } from 'react';
import {
  Button, Form, Modal, Spinner,
} from 'react-bootstrap';
import Error from '../../../Components/Error';
import KnowledgeBaseService, { AUDIENCES } from '../../../Services/KnowledgeBaseService';

/**
 * Create a knowledge base. Same shape as DiscountCodeInputModal: a controlled
 * form, the service call, `onSuccess` to refetch, `handleClose` to dismiss.
 *
 * Create only, no edit — the contract has no update verb, and inventing one
 * in the UI would make an unsaveable form.
 */

const AUDIENCE_HELP = {
  [AUDIENCES.INTERNAL]: 'Office and operators only. Nothing here is quoted to a customer.',
  [AUDIENCES.CUSTOMER_SAFE]: 'May be quoted back to a customer verbatim. Put only approved copy here.',
};

function KnowledgeBaseInputModal({ show = false, handleClose, onSuccess }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [audience, setAudience] = useState(AUDIENCES.INTERNAL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSave = name.trim().length > 0 && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      await KnowledgeBaseService.create({
        name: name.trim(),
        description: description.trim(),
        audience,
      });
      if (onSuccess) onSuccess();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create the knowledge base');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>New knowledge base</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Error error={error} setError={setError} />

          <Form.Group className="mb-3" controlId="kb-name">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Operations handbook"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="kb-description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What belongs in here, so the next person knows where to put a document."
            />
          </Form.Group>

          <Form.Group className="mb-2" controlId="kb-audience">
            <Form.Label>Audience</Form.Label>
            <Form.Select value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option value={AUDIENCES.INTERNAL}>Internal</option>
              <option value={AUDIENCES.CUSTOMER_SAFE}>Customer-safe</option>
            </Form.Select>
            <Form.Text className="text-muted">{AUDIENCE_HELP[audience]}</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!canSave}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating…
              </>
            ) : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default KnowledgeBaseInputModal;
